import React, { useMemo, useState } from 'react';

import { Card, Alert, Spinner, Input, Badge, ProgressBar } from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';

import { formatTry } from '../format';
import SpendingSummary from './SpendingSummary';
import SpendingToolbar from './SpendingToolbar';
import useSpendingReport from './useSpendingReport';
import { categoryName, percentOf } from './spendingFormat';
import styles from './SpendingReports.module.css';

const VISIBLE_STORES = 25;

/** Where the money went: stores ranked by combined spending. */
export default function SpendingByStoreReport() {
  const { t, language } = useTranslation();
  const { range, changeRange, scope, setScope, report, loading, error } = useSpendingReport();
  const [query, setQuery] = useState('');

  const categoriesByKey = useMemo(() => {
    const map = {};
    for (const c of report?.categories || []) map[c.key] = c;
    return map;
  }, [report]);

  const stores = useMemo(() => {
    const all = report?.stores || [];
    const q = query.trim().toLocaleLowerCase(language);
    const filtered = q ? all.filter((s) => s.name.toLocaleLowerCase(language).includes(q)) : all;
    return filtered.slice(0, VISIBLE_STORES);
  }, [report, query, language]);

  const top = Number(report?.stores?.[0]?.total || 0);
  const hasData = report && Number(report.total) > 0;

  return (
    <Card>
      <Card.Header>
        <div className={styles.headerRow}>
          <h5 className={styles.title}>{t('budget.reportSpendingByStore')}</h5>
          <SpendingToolbar range={range} onRangeChange={changeRange} scope={scope} onScopeChange={setScope} />
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <Spinner size="sm" label={t('loading')} />}
        {!loading && !error && !hasData && <div className={styles.empty}>{t('budget.spendingEmpty')}</div>}
        {!loading && hasData && (
          <>
            <SpendingSummary report={report} />
            <div className={styles.search}>
              <Input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('budget.spendingSearchStores')}
                aria-label={t('budget.spendingSearchStores')}
              />
            </div>
            {stores.length === 0 && <div className={styles.empty}>{t('noData')}</div>}
            <ol className={styles.rows}>
              {stores.map((s) => (
                <li key={s.name} className={styles.row}>
                  <div className={styles.rowHead}>
                    <span className={styles.rowName}>
                      {s.name}
                      <Badge variant="neutral">{categoryName(categoriesByKey[s.categoryKey], t, language)}</Badge>
                    </span>
                    <span className={styles.rowAmount}>{formatTry(s.total)}</span>
                  </div>
                  <ProgressBar now={percentOf(s.total, top)} aria-hidden="true" />
                  <div className={styles.rowMeta}>
                    {t('budget.spendingStoreMeta', {
                      count: s.count,
                      receipts: s.receiptCount,
                      percent: percentOf(s.total, report.total),
                    })}
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
